import {
  Component,
  EventEmitter,
  inject,
  Input,
  input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Store } from '@ngrx/store';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';
import AwsS3, { AwsS3UploadParameters } from '@uppy/aws-s3';
import Uppy, { Meta, UploadResult } from '@uppy/core';
import axios from 'axios';
import { Subject, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import { UserModel } from '../../../../models/user.model';
import { UtilsService } from '../../../../services/utils.service';
import { AppState } from '../../../../state/appstate';

@Component({
  selector: 'app-s3-document-upload',
  standalone: true,
  imports: [
    UppyAngularDashboardModule,
    UppyAngularDashboardModalModule,
    UppyAngularDragDropModule,
    UppyAngularProgressBarModule,
    UppyAngularStatusBarModule,
  ],
  templateUrl: './s3-document-upload.component.html',
  styleUrl: './s3-document-upload.component.css',
})
export class S3DocumentUploadComponent implements OnInit, OnDestroy {
  store: Store<AppState> = inject(Store<AppState>);
  private fbAuth: Auth = inject(Auth);

  label = input<string | undefined>();
  notes = input<string[] | undefined>();
  maxFileSize = input<number | undefined>();
  @Input() formats: string[] | undefined;

  preSignedUrlEndpoint = input.required<string>();
  cloudFrontDomain = input.required<string>();
  apikey = input.required<string>();
  metadata: any | undefined = undefined;
  inputParams: any | undefined = undefined;

  @Output() uploadStatus = new EventEmitter<{
    success: boolean;
    response?: any;
    error?: any;
  }>();

  user: UserModel | null = null;

  exit$: Subject<void> = new Subject<void>();

  uppy!: Uppy;

  @Output() isAdded: EventEmitter<boolean> = new EventEmitter();

  isAttached: boolean = false;

  dashboardProps = {
    inline: true,
    hideUploadButton: true,
    hideRetryButton: true,
    hidePauseResumeButton: true,
    hideProgressAfterFinish: true,
    showSelectedFiles: true,
    showProgressDetails: true,
    proudlyDisplayPoweredByUppy: false,
    width: '100%',
    height: '130px',
  };

  constructor() {
    // this.uppy = new Uppy({
    //   autoProceed: false,
    //   restrictions: {
    //     maxFileSize: 5242880, // 5 MB
    //     maxNumberOfFiles: 1,
    //     minNumberOfFiles: 1,
    //     allowedFileTypes: this.formats ?? ['*'],
    //   },
    // });

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  // toggleEnableUpload(): void {
  //   const dashboard = this.uppy.getPlugin('AwsS3') as any;
  //   if (dashboard) {
  //     dashboard.setOptions({ disabled: !isEnabled });
  //   }
  // }

  ngOnInit(): void {
    this.uppy = new Uppy({
      autoProceed: false,
      restrictions: {
        maxFileSize: this.maxFileSize() ?? 5242880, // 5 MB
        maxNumberOfFiles: 1,
        minNumberOfFiles: 1,
        allowedFileTypes: this.formats ?? ['*'],
      },
    });

    this.uppy.use(AwsS3, {
      limit: 1,
      getUploadParameters: this.getSignedUrl.bind(this),
      shouldUseMultipart: false,
    });

    this.uppy.on('file-added', (file) => {
      this.isAdded.emit(true);
      this.isAttached = true;
    });

    this.uppy.on('file-removed', (file) => {
      this.isAdded.emit(false);
      this.isAttached = false;
    });

    this.uppy.on('complete', (result) => {
      this.uploadStatus.emit({ success: true, response: result });
      this.clearFiles();
    });
  }

  ngOnDestroy(): void {
    this.uppy.destroy();
    this.exit$.next();
    this.exit$.complete();
  }

  getIsAttached(): boolean {
    return this.isAttached;
  }

  setInputParams(inputParams: any): void {
    this.inputParams = inputParams;
  }

  setMetadata(metadata: any): void {
    this.metadata = metadata;
  }

  async getSignedUrl(file: any, options: any): Promise<AwsS3UploadParameters> {
    // TODO User not found alert
    if (this.user?.uid === null) {
      throw new Error('Unsuccessful request', { cause: 'User not found' });
    }

    const fileExtn: string | undefined = file.name?.split('.')?.pop();
    const token: string | undefined =
      await this.fbAuth.currentUser?.getIdToken();

    if (token === undefined) {
      throw new Error('Unsuccessful request', {
        cause: 'Failed to authenticate user',
      });
    }

    try {
      const response = await axios.get<any>(this.preSignedUrlEndpoint(), {
        params: {
          createdById: this.user?.uid,
          name: file.name,
          fileExtension: fileExtn,
          metadata: this.metadata,
          ...this.inputParams,
        },
        headers: {
          'x-api-key': this.apikey(),
          AuthorizationToken: `Bearer ${token}`,
          'Content-Type': UtilsService.contentType(fileExtn ?? 'json'),
        },
      });

      if (!(response.status === 200)) {
        this.uppy.clear();
        const exception: AppExceptionModel = {
          code: 'ERROR_PRESIGNED_URL',
          title: 'Error approving upload',
          message: response.data.message ?? 'Failed to get upload URL',
        };
        throw exception;
      }

      // Parse the JSON response.
      const data = await response.data;

      return {
        method: 'PUT',
        url: data.url,
        headers: {
          'Content-Type': file.type,
        },
      };
    } catch (error: any) {
      this.uppy.clear();
      if (axios.isAxiosError(error)) {
        // console.error('Error status:', error.response?.status);
        // console.error('Backend message:', error.response?.data);
        const backendBody = error.response?.data;
        const parsedBody =
          typeof backendBody === 'string'
            ? JSON.parse(backendBody)
            : backendBody;
        // console.error('Parsed message:', parsedBody.message);
        const exception: AppExceptionModel = {
          code: 'ERROR_PRESIGNED_URL',
          title: 'Error Approving Upload',
          message: parsedBody.message ?? 'Failed to get upload URL',
        };
        throw exception;
      } else {
        const exception: AppExceptionModel = {
          code: 'ERROR_PRESIGNED_URL',
          title: 'Error Approving Upload',
          message: 'Failed to get upload URL',
        };
        throw exception;
      }
    }
  }

  async upload(): Promise<
    UploadResult<Meta, Record<string, never>> | undefined
  > {
    if (this.uppy?.getFiles().length === 0) {
      throw new Error('No files to upload');
    }

    try {
      const result = await this.uppy.upload();
      return result;
    } catch (error) {
      this.uploadStatus.emit({ success: false, error });
      this.clearFiles();
      throw error;
    }
  }

  clearFiles() {
    this.uppy.cancelAll(); // cancel in-progress uploads
    const files = this.uppy.getFiles();
    for (const file of files) {
      this.uppy.removeFile(file.id);
    }
  }
}
